import React, { useState } from 'react';
import {
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseIcon from '@mui/icons-material/Close';
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../context/LanguageContext';
import LaunchIcon from '@mui/icons-material/Launch'; // Add this import

const DrawerComp = ({ links }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate(); // Add navigate hook

  // Map Swedish menu items to translation keys and routes
  const getTranslationKey = (swedishText) => {
    const translationMap = {
      'Hem': 'nav.home',
      'Boende': 'nav.rooms', 
      'Frukost': 'nav.breakfast',
      'Galleri': 'nav.gallery',
      'Restaurang': 'nav.restaurant', // Add this line
      'Boka': 'nav.book',
      'Faq': 'nav.faq',
      'Kontakt': 'nav.kontakt'
    };
    return translationMap[swedishText] || swedishText;
  };

  // Get route path for Swedish menu items
  const getRoutePath = (swedishText) => {
    const routeMap = {
      'Hem': '/',
      'Boende': '/boende',
      'Frukost': '/frukost',
      'Galleri': '/galleri', 
      'Boka': '/boka',
      'Faq': '/faq',
      'Kontakt': '/kontakt'
      // Don't add Restaurang here - it's an external link
    };
    return routeMap[swedishText] || '/';
  };

  // Language options
  const languages = [
    { code: 'sv', name: 'Svenska', shortCode: 'SV', flag: '🇸🇪' },
    { code: 'en', name: 'English', shortCode: 'EN', flag: '🇺🇸' },
    { code: 'de', name: 'Deutsch', shortCode: 'DE', flag: '🇩🇪' }
  ];

  const currentLanguage = languages.find(lang => lang.code === language);

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setOpen(false);
  };

  const handleMenuItemClick = (link) => {
    if (link === 'Restaurang') {
      // Open external link in new tab
      window.open('https://slitebaren.se/', '_blank', 'noopener,noreferrer');
    } else {
      // Navigate to internal route
      navigate(getRoutePath(link));
    }
    setOpen(false);
  };

  return (
    <>
      <Drawer 
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(9, 159, 190, 0.98)',
            width: '100vw',
            height: '100vh',
            padding: '20px',
          }
        }}
      >
        {/* Close Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '40px' }}>
          <IconButton 
            onClick={() => setOpen(false)}
            sx={{ color: 'white', fontSize: '2rem' }}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </Box>

        {/* Menu Items */}
        <List sx={{ flex: 1 }}>
          {links.map((link, index) => (
            <ListItemButton 
              key={index}
              onClick={() => handleMenuItemClick(link)}
              sx={{
                padding: '20px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <ListItemText 
                primary={
                  <Typography 
                    sx={{ 
                      color: 'white', 
                      fontSize: '1.2rem', 
                      fontWeight: 'bold',
                      textAlign:'center'
                    }}
                  >
                    {t(getTranslationKey(link))}
                  </Typography>
                } 
              />
              {/* Show external link icon for Restaurang */}
              {link === 'Restaurang' && (
                <LaunchIcon sx={{ color: 'white', fontSize: '1.2rem', ml: 2 }} />
              )}
            </ListItemButton>
          ))}
        </List>

        {/* Language Selector - Keep your existing code here */}
        {/* ... */}
      </Drawer>
      
      <IconButton 
        sx={{ marginLeft: 'auto', color: 'white' }} 
        onClick={() => setOpen(!open)}
      >
        <MenuRoundedIcon sx={{ fontSize: '2rem' }} />
      </IconButton>
    </>
  );
};

export default DrawerComp;